import { useState } from 'react';
import type { ReactNode } from 'react';

import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { Alert, Button, CircularProgress, Stack, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';

import { ConfirmSyncDialog } from './ConfirmSyncDialog';
import { FieldRow } from './FieldRow';

export interface SyncTriggerRowProps {
  /** `FieldRow` label. */
  label: ReactNode;
  /** `FieldRow` description — says WHAT moves WHERE. Not every row has one. */
  description?: ReactNode;
  buttonLabel: string;
  /** Error-coloured button + a mandatory acknowledgement checkbox in the confirm dialog. */
  destructive?: boolean;
  /**
   * Confirm-dialog copy. Omit for an ordinary, reversible, tenant-scoped trigger — it then runs
   * immediately on click, no dialog. Provide it for anything destructive or otherwise
   * consequential (`warning` renders a standout banner; required in practice when `destructive`).
   */
  confirm?: { title: string; body?: ReactNode; warning?: string };
  /** Runs the trigger; resolves the upstream's verbatim `text/plain` message. */
  run: () => Promise<string>;
  /** Shown when `run()` resolves to an empty string. */
  successFallback?: string;
  /** Shown instead of `error.message` when the failure is a `403` (e.g. a dev-only operation). */
  forbiddenMessage?: string;
  /** Permission gate — disables the button and shows a "no permission" tooltip. */
  disabled?: boolean;
  /** Button icon (pending state always shows a spinner instead). Defaults to a generic sync glyph. */
  icon?: ReactNode;
  /** Optional parameter controls, rendered above the button. */
  children?: ReactNode;
}

/**
 * One trigger action on the Sync tab — a button plus its own mutation, an optional
 * confirm step, and an inline result `Alert` (the upstream's plain-text message, success or
 * error). Every result is announced via MUI `Alert`'s `role="alert"` and persists until the next
 * run.
 *
 * As of adr-integration-012/adr-integration-013 (2026-08-30) every one of these triggers is
 * tenant-scoped, so `confirm` is opt-in rather than mandatory — most rows on this screen omit it
 * and fire immediately; only the destructive delete-all rows still ask first.
 *
 * Deliberately not `TestConnectionButton`: that component's contract is
 * `Promise<TestConnectionResult>` with a fixed icon and no confirm step, none of which fit a
 * trigger whose payload is a plain string.
 */
export function SyncTriggerRow({
  label,
  description,
  buttonLabel,
  destructive = false,
  confirm,
  run,
  successFallback,
  forbiddenMessage,
  disabled = false,
  icon,
  children,
}: SyncTriggerRowProps) {
  const { t } = useTranslation('settings');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mutation = useMutation<string, NormalizedApiError, void>({ mutationFn: () => run() });

  const fire = () => {
    setConfirmOpen(false);
    mutation.mutate();
  };

  const handleTrigger = () => {
    if (confirm) {
      setConfirmOpen(true);
    } else {
      fire();
    }
  };

  const resultText = mutation.data === '' ? successFallback : mutation.data;
  const errorText =
    mutation.error?.status === 403 && forbiddenMessage ? forbiddenMessage : mutation.error?.message;

  const button = (
    <Button
      variant="outlined"
      color={destructive ? 'error' : 'primary'}
      onClick={handleTrigger}
      disabled={disabled || mutation.isPending}
      startIcon={
        mutation.isPending ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          (icon ?? <SyncRoundedIcon />)
        )
      }
    >
      {buttonLabel}
    </Button>
  );

  return (
    <FieldRow alignTop label={label} description={description}>
      <Stack spacing={1.5} alignItems="flex-start">
        {children}
        {disabled ? (
          <Tooltip title={t('retailSettings.noPermissionTooltip')}>
            <span>{button}</span>
          </Tooltip>
        ) : (
          button
        )}
        {mutation.isError ? (
          <Alert severity="error" sx={{ width: '100%' }}>
            {errorText}
          </Alert>
        ) : null}
        {mutation.isSuccess && resultText ? (
          <Alert severity="success" sx={{ width: '100%' }}>
            {resultText}
          </Alert>
        ) : null}
      </Stack>

      {confirm ? (
        <ConfirmSyncDialog
          open={confirmOpen}
          title={confirm.title}
          body={confirm.body}
          warning={confirm.warning}
          destructive={destructive}
          ackLabel={t('retailSettings.sync.destructiveAck')}
          confirmLabel={buttonLabel}
          onConfirm={fire}
          onCancel={() => setConfirmOpen(false)}
        />
      ) : null}
    </FieldRow>
  );
}
